import {css, StyleSheet} from 'aphrodite';
import produce, {castDraft} from 'immer';
import React, {ReactNode, useState} from 'react';
import isEqual from 'react-fast-compare';

import {isFormErrorsEmpty} from 'lookup-config/validateLookupSteps';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Tooltip from 'ui-library/bs/Tooltip';
import {ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextField from 'ui-library/inputs/TextField';
import Modal, {ModalProps} from 'ui-library/modals/Modal';

import {AddLookupStepButton} from './AddLookupStepButton';
import ConfigStepsEditor from './ConfigStepsEditor';
import {FilterLookupGroupPopover} from './FilterLookupGroupPopover';
import FirstLookupStepFormSection, {
  FirstLookupStepFormProps,
} from './first-lookup-step/FirstLookupStepFormSection';
import {BasePaneLookupObjectValidation} from './LookupConfigModal';
import {
  FormErrors,
  LookupConfigModalContextInfo,
  LookupStepInfo,
  LookupStepTreeItemsById,
  LookupStepType,
} from './LookupConfigModalConstants';
import {MAX_SCHEMA_FIELD_DEPTH} from './LookupConfigModalConstants';
import {LookupConfigModalPaneIndicator} from './LookupConfigModalPaneIndicator';
import LookupStepForm, {isLookupStepConfigurable} from './LookupStepForm';
import {lookupStepInfosToGroups, MAX_LOOKUP_STEP_FORMS} from './LookupStepInitializerUtils';
import LookupStepModifierForm from './LookupStepModifierForm';
import {LookupStepSectionHeader} from './LookupStepSectionHeader';
import SchemaFieldSelectTree from './SchemaFieldSelectTree';
import validateLookupSteps from './validateLookupSteps';

export interface LookupConfigBasePaneProps<TExtraState> {
  modalProps?: Partial<ModalProps>;
  context?: LookupConfigModalContextInfo;
  renderFirstLookupStepForm?: (props: FirstLookupStepFormProps<TExtraState>) => JSX.Element;
  extraState: TExtraState;
  setExtraState: (extraData: TExtraState) => void;
  shouldAlwaysShowFirstSelectedItemInTree?: boolean;
  schemaFieldFilterFn?: (field: SchemaField, excludeNonLinkFields: boolean) => boolean;
  allowAlternatingGridPatterns?: boolean;
  shouldShowNullForEmptyContentsTooltip?: boolean;
  baseSchema: Schema | null;
  firstLookupStepTypes: Array<LookupStepType>;
  shouldHideFilter?: boolean;
  allowInventoryFilters?: boolean;
  intermediateLookupStepTypes: Array<LookupStepType>;
  lookupStepInfos: Array<LookupStepInfo>;
  title: string;
  onEditLookupStepInfos: () => void;
  onGoToNextPane: (lookupStepInfos: Array<LookupStepInfo>) => void;
  onRequestClose: () => void;
  lookupConfigLabel: string | undefined;
  setLookupConfigLabel: (label: string) => void;
  useSchemaLanguage?: boolean;
  description?: ReactNode;
  continueButtonDataTestAttrs?: DataTestAttrs;
  modalDataTestAttrs?: DataTestAttrs;
  schemaFieldTreeFilterFn?: (field: SchemaField) => boolean;
  basePaneLookupObjectValidation?: BasePaneLookupObjectValidation;
  selectTreeItemsById: LookupStepTreeItemsById;
  setSelectTreeItemsById: (items: LookupStepTreeItemsById) => void;
  typecastSchemasByNodeId: Map<string, Schema | null>;
  updateTypecastSchemasByNodeId: (nodeId: string, typecastSchema: Schema | null) => void;
}

export default function LookupConfigBasePane<TExtraState>(
  props: LookupConfigBasePaneProps<TExtraState>
): JSX.Element {
  const [lookupStepGroups, setLookupStepGroups] = useState(lookupStepInfosToGroups(props.lookupStepInfos));

  const {onEditLookupStepInfos} = props;
  const setLookupStepGroupAtIndex = React.useCallback(
    (lookupStepGroup: Array<LookupStepInfo>, index: number) => {
      const newLookupStepGroups = produce(lookupStepGroups, (draftLookupStepGroups) => {
        draftLookupStepGroups[index] = castDraft(lookupStepGroup);
        // Length of lookupStepGroups should always stay the same.
        for (let i = index + 1; i < draftLookupStepGroups.length; i++) {
          draftLookupStepGroups[i] = castDraft([]);
        }
      });
      setLookupStepGroups(newLookupStepGroups);
      onEditLookupStepInfos();
    },
    [lookupStepGroups, onEditLookupStepInfos]
  );

  const lastGroupIndex = lookupStepGroups.map((group) => group.length !== 0).lastIndexOf(true);
  const [allFormErrors, setAllFormErrors] = useState(
    lookupStepGroups.map((group, index) =>
      validateLookupSteps(
        group,
        props.baseSchema,
        index === lastGroupIndex
          ? props.basePaneLookupObjectValidation?.basePaneInvalidLookupObjectFn
          : undefined
      )
    )
  );

  const setFormErrorsAtIndex = (formErrors: FormErrors, index: number): void => {
    const newFormErrors = [...allFormErrors];
    newFormErrors[index] = formErrors;
    setAllFormErrors(newFormErrors);
  };

  function renderFirstLookupStepFormSection(args: {
    handleSetFirstConfigStep: (lookupStepGroup: Array<LookupStepInfo>) => void;
    handleAddConfigStep: (lookupStepGroup: Array<LookupStepInfo>) => void;
    isCollapsed: boolean;
    setIsCollapsed: (isCollapsed: boolean) => void;
    formErrors: FormErrors;
    setFormErrors: (formErrors: FormErrors) => void;
  }): ReactNode {
    return (
      <FirstLookupStepFormSection
        context={props.context}
        schemaFieldFilterFn={props.schemaFieldFilterFn}
        schemaFieldTreeFilterFn={props.schemaFieldTreeFilterFn}
        firstLookupStepTypes={props.firstLookupStepTypes}
        shouldHideFilter={props.shouldHideFilter}
        allowInventoryFilters={props.allowInventoryFilters}
        intermediateLookupStepTypes={props.intermediateLookupStepTypes}
        baseSchema={props.baseSchema}
        disabled={lookupStepGroups[1].length > 0}
        isCollapsed={args.isCollapsed}
        setIsCollapsed={args.setIsCollapsed}
        lookupStepGroup={lookupStepGroups[0]}
        handleSetLookupStepGroup={args.handleSetFirstConfigStep}
        handleAddLookupStepGroup={args.handleAddConfigStep}
        formErrors={args.formErrors}
        setFormErrors={args.setFormErrors}
        basePaneInvalidLookupObjectErrorMessage={
          props.basePaneLookupObjectValidation?.basePaneInvalidLookupObjectErrorMessage
        }
        useSchemaLanguage={props.useSchemaLanguage}
        renderFirstLookupStepForm={props.renderFirstLookupStepForm}
        extraState={props.extraState}
        setExtraState={props.setExtraState}
        shouldAlwaysShowFirstSelectedItemInTree={props.shouldAlwaysShowFirstSelectedItemInTree}
        allowAlternatingGridPatterns={props.allowAlternatingGridPatterns}
        selectTreeItemsById={props.selectTreeItemsById}
        setSelectTreeItemsById={props.setSelectTreeItemsById}
        typecastSchemasByNodeId={props.typecastSchemasByNodeId}
        updateTypecastSchemasByNodeId={props.updateTypecastSchemasByNodeId}
      />
    );
  }

  function renderLookupStepSectionHeader(args: {
    isCollapsed: boolean;
    setIsCollapsed: (isCollapsed: boolean) => void;
    isLastStep: boolean;
    configStep: Array<LookupStepInfo>;
    handleRemoveConfigStep: () => void;
    formErrors: FormErrors;
  }): JSX.Element {
    return (
      <LookupStepSectionHeader
        isCollapsed={args.isCollapsed}
        setIsCollapsed={args.setIsCollapsed}
        isActive={args.isLastStep}
        lookupStepGroup={args.configStep}
        handleRemoveLookupStepGroup={args.isLastStep ? args.handleRemoveConfigStep : null}
        parentSchema={null}
        error={args.formErrors.missingField ? 'Missing field' : null}
      />
    );
  }

  function getPreviousLookupStepInfo(): LookupStepInfo | null {
    const nonEmptyLookupStepGroups = lookupStepGroups.filter((lookupStepGroup) => lookupStepGroup.length > 0);
    if (nonEmptyLookupStepGroups.length < 2) {
      return null;
    }
    const previousLookupStepGroup = lookupStepGroups[nonEmptyLookupStepGroups.length - 2];
    return previousLookupStepGroup[previousLookupStepGroup.length - 1];
  }

  function renderLookupStepForm(args: {
    isLastStep: boolean;
    configStep: Array<LookupStepInfo>;
    handleUpdateConfigStep: (configStep: Array<LookupStepInfo>) => void;
    formErrors: FormErrors;
  }): JSX.Element | null {
    const firstStep = args.configStep[0];
    if (!isLookupStepConfigurable(firstStep.lookupStep)) {
      return null;
    }
    return (
      <>
        <LookupStepForm
          disabled={!args.isLastStep}
          lookupStepInfo={firstStep}
          previousStepInfo={getPreviousLookupStepInfo()}
          handleSetLookupStep={(lookupStepInfo: LookupStepInfo) => {
            args.handleUpdateConfigStep([lookupStepInfo]);
          }}
          context={props.context}
          allowAlternatingGridPatterns={props.allowAlternatingGridPatterns}
          shouldShowNullForEmptyContentsTooltip={props.shouldShowNullForEmptyContentsTooltip}
          schemaFieldFilterFn={props.schemaFieldFilterFn}
          formErrors={args.formErrors}
          isFirstStep={false}
          basePaneInvalidLookupObjectErrorMessage={
            props.basePaneLookupObjectValidation?.basePaneInvalidLookupObjectErrorMessage
          }
        />
        <LookupStepModifierForm
          lookupStepInfo={firstStep}
          intermediateLookupStepTypes={props.intermediateLookupStepTypes}
          disabled={!args.isLastStep}
          isProcessOutputContext={props.context?.processTaskSchema != null}
          handleSetLookupStep={(lookupStepInfo: LookupStepInfo) => {
            args.handleUpdateConfigStep([lookupStepInfo]);
          }}
        />
      </>
    );
  }

  function renderConfigStepFormChildren(args: {
    isLastStep: boolean;
    configStep: Array<LookupStepInfo>;
    handleUpdateConfigStep: (configStep: Array<LookupStepInfo>) => void;
    formErrors: FormErrors;
  }): JSX.Element {
    return (
      <>
        <SchemaFieldSelectTree
          label={props.useSchemaLanguage ? 'Schema' : undefined}
          disabled={!args.isLastStep}
          hasError={args.formErrors.missingField}
          lookupStepGroup={args.configStep}
          handleSetLookupStepGroup={(lookupStepGroup: Array<LookupStepInfo>) => {
            // The schema field tree creates new lookupStepInfos for each link in the tree. The first
            // lookupStepInfo might have modifiers on it so we do not want the schema field tree to
            // overwrite it. If the first lookup step has changed (e.g. by the user selecting a typecast of
            // a schema interface step), the modifiers (attached to the lookupStepInfo object) should be dropped
            // as they will no longer be valid on the typecasted step. Otherwise, keep the modifiers.
            const prevFirstLookupStepInfo = args.configStep[0];
            const newFirstLookupStepInfo = lookupStepGroup[0];
            const didFirstLookupStepChange = !isEqual(
              prevFirstLookupStepInfo.lookupStep,
              newFirstLookupStepInfo.lookupStep
            );
            const firstLookupStepInfoToSet = didFirstLookupStepChange
              ? newFirstLookupStepInfo
              : prevFirstLookupStepInfo;

            args.handleUpdateConfigStep([firstLookupStepInfoToSet, ...lookupStepGroup.slice(1)]);
          }}
          depth={MAX_SCHEMA_FIELD_DEPTH}
          schemaFieldTreeFilterFn={props.schemaFieldTreeFilterFn}
          selectTreeItemsById={props.selectTreeItemsById}
          setSelectTreeItemsById={props.setSelectTreeItemsById}
          typecastSchemasByNodeId={props.typecastSchemasByNodeId}
          updateTypecastSchemasByNodeId={props.updateTypecastSchemasByNodeId}
        />
        {!props.shouldHideFilter && (
          <FilterLookupGroupPopover
            disabled={!args.isLastStep}
            lookupStepGroup={args.configStep}
            handleSetLookupStepGroup={args.handleUpdateConfigStep}
            allowInventoryFilters={props.allowInventoryFilters}
            schemaFieldFilterFn={props.schemaFieldFilterFn}
          />
        )}
      </>
    );
  }

  function renderAddConfigStepButton(args: {
    disabled: boolean;
    configStep: Array<LookupStepInfo>;
    setFormErrors: (formErrors: FormErrors) => void;
    handleAddConfigStep: (lookupStepGroup: Array<LookupStepInfo>) => void;
  }): JSX.Element {
    return (
      <Tooltip
        disabled={!args.disabled}
        title={`The maximum number of lookup steps is ${MAX_LOOKUP_STEP_FORMS}`}
      >
        <AddLookupStepButton
          disabled={args.disabled}
          previousStepGroup={args.configStep}
          setFormErrors={args.setFormErrors}
          handleSetLookupStep={(lookupStepInfo: LookupStepInfo) => {
            args.handleAddConfigStep([lookupStepInfo]);
          }}
          context={props.context}
          allowedLookupStepTypes={props.intermediateLookupStepTypes}
          baseSchema={props.baseSchema}
        />
      </Tooltip>
    );
  }

  return (
    <Modal
      {...props.modalProps}
      buttons={[
        {
          key: 'next',
          text: 'Continue',
          style: ButtonStyle.SECONDARY,
          onClick: () => {
            const lastStep = lookupStepGroups[lastGroupIndex];
            const newFormErrors = validateLookupSteps(
              lastStep,
              props.baseSchema,
              props.basePaneLookupObjectValidation?.basePaneInvalidLookupObjectFn
            );
            const newAllFormErrors = [...allFormErrors];
            newAllFormErrors[lastGroupIndex] = newFormErrors;
            setAllFormErrors(newAllFormErrors);
            if (newAllFormErrors.every((formErrors) => isFormErrorsEmpty(formErrors))) {
              props.onGoToNextPane(lookupStepGroups.flat());
            } else {
              props.basePaneLookupObjectValidation?.onBasePaneSubmissionError?.();
            }
          },
          disabled: lookupStepGroups.flat().length === 0,
          dataTestAttrs: props.continueButtonDataTestAttrs,
        },
      ]}
      onRequestClose={props.onRequestClose}
      title={props.title}
      scrollable={true}
      dataTestAttrs={props.modalDataTestAttrs}
    >
      <ConfigStepsEditor
        allFormErrors={allFormErrors}
        setFormErrorsAtIndex={setFormErrorsAtIndex}
        description={props.description}
        configSteps={lookupStepGroups}
        setConfigStepAtIndex={setLookupStepGroupAtIndex}
        getEmptyConfigStep={() => []}
        canAddConfigStep={(group: Array<LookupStepInfo> | null) => group != null}
        isEmptyStep={(group: Array<LookupStepInfo>) => group.length === 0}
        renderFirstConfigStepFormSection={renderFirstLookupStepFormSection}
        renderConfigStepHeader={renderLookupStepSectionHeader}
        renderConfigStepForm={renderLookupStepForm}
        renderConfigStepFormChildren={renderConfigStepFormChildren}
        renderAddConfigStepButton={renderAddConfigStepButton}
      >
        <div className="u-moderate-spacing-bottom">
          <LookupConfigModalPaneIndicator paneIndex={0} useSchemaLanguage={props.useSchemaLanguage} />
        </div>
        {props.lookupConfigLabel != null && (
          <TextField
            className={css(styles.field)}
            label="Destination label"
            onChange={(e) => {
              props.setLookupConfigLabel(e.target.value);
            }}
            labelStyle={FieldLabel.STYLES.LABEL}
            isRequired={true}
            value={props.lookupConfigLabel}
          />
        )}
      </ConfigStepsEditor>
    </Modal>
  );
}

const styles = StyleSheet.create({
  field: {
    width: '50%',
    marginBottom: '8px',
  },
});
